import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root'
})
export class DataFormatService {
  
  constructor() {}
  
  /**
   * Converts a date string (UTC ISO 8601 or local) to a Date object for use in bsDatepicker.
   * @param dateString The date string from the backend (could be UTC ISO 8601 or local).
   * @returns A Date object or null if the input is invalid.
   */
  convertToDatePickerFormat(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;
    
    let date = new Date(dateString);
    
    return isNaN(date.getTime()) ? null : date;
  }
  
  /**
   * Converts a Date object, NgbDateStruct, or date string to a UTC ISO 8601 string for sending to the backend.
   * @param date The Date object, NgbDateStruct, or date string to convert.
   * @returns A UTC ISO 8601 string or null if the input is invalid.
   */
  convertToISO8601(date: Date | NgbDateStruct | string | null): string | null {
    if (!date) {
      return null;
    }

    let dateObj: Date;

    // Si es un NgbDateStruct, convertirlo a Date
    if (this.isNgbDateStruct(date)) {
      dateObj = new Date(date.year, date.month - 1, date.day);
    } 
    // Si es un Date, usarlo directamente
    else if (date instanceof Date) {
      dateObj = date;
    }
    // Si es un string, parsearlo (formato DD/MM/YYYY)
    else if (typeof date === 'string') {
      const parts = date.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          dateObj = new Date(year, month - 1, day);
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
    // Si no es ninguno de los tipos esperados, retornar null
    else {
      return null;
    }

    // Verificar que la fecha sea válida
    if (isNaN(dateObj.getTime())) {
      return null;
    }
  
    // Create a new date from the original date.
    const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
    return localDate.toISOString();
  }

  /**
   * Type guard to check if an object is NgbDateStruct
   */
  private isNgbDateStruct(obj: any): obj is NgbDateStruct {
    return obj && typeof obj === 'object' && 
           typeof obj.year === 'number' && 
           typeof obj.month === 'number' && 
           typeof obj.day === 'number';
  }

  /**
   * Removes the country code from a phone number if it starts with a "+" followed by digits.
   * @param phoneNumber The phone number string (e.g., "+34123456789").
   * @returns The phone number without the country code.
   */
  removeCountryCode(phoneNumber: string): string {
    return phoneNumber.startsWith('+') ? phoneNumber.replace(/^\+\d{1,2}/, '') : phoneNumber;
  }

  /**
   * Extracts the country code from a phone number if it starts with a "+" followed by digits.
   * @param phoneNumber The phone number string (e.g., "+34123456789").
   * @returns The extracted country code (e.g., "+34") or an empty string if none is found.
   */
  getCountryCode(phoneNumber: string): string {
    const match = phoneNumber.match(/^(\+\d{1,2})/);
    return match ? match[1] : '';
  }

  /**
   * Extracts a specific field from a JWT token.
   * @param token The JWT token.
   * @param field The field to extract from the token payload.
   * @returns The value of the specified field or null if not found.
   */
  extractFieldFromToken(token: string, field: string): string | null {
    if (!token) return null;
    try {
      const payloadBase64 = token.split('.')[1]; // Extract payload part
      const payloadJson = JSON.parse(atob(payloadBase64)); // Decode Base64 and parse JSON
      return payloadJson[field] || null;
    } catch (error) {
      console.error('Invalid token format:', error);
      return null;
    }
  }
}

